/**
 * PreviousActionsPanel — sprint 375 · uses shared ActionDetailModal.
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { X, AlertTriangle } from 'lucide-react';
import { ActionDetailModal } from './ActionDetailModal';

function _isOverdue(a) {
  if (!a.due_date || a.status === 'done' || a.status === 'dropped') return false;
  return a.due_date < new Date().toISOString().slice(0, 10);
}

export function PreviousActionsPanel({ reviewId, onClose }) {
  const { API, getHeaders } = useAuth();
  const [prev, setPrev] = useState({ items: [], previous_review: null });
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reviews/${reviewId}/previous-actions`, { headers: getHeaders() });
      if (r.ok) setPrev(await r.json());
    } catch { toast.error('Failed to load previous actions'); }
  }, [API, getHeaders, reviewId]);
  useEffect(() => { load(); }, [load]);

  const items = prev.items || [];
  return (
    <div className="h-full flex flex-col bg-white border-l border-[#E7EBE6]" data-testid="previous-actions-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E7EBE6] bg-[#F9FBF7]">
        <div>
          <h3 className="text-sm font-semibold text-[color:var(--color-text-primary)]">Previous Actions</h3>
          {prev.previous_review && (
            <p className="text-[10px] text-[#7A8B82]" data-testid="pa-prev-title">
              from {prev.previous_review.title}
              {prev.previous_review.reviewed_at && ` · reviewed ${new Date(prev.previous_review.reviewed_at).toLocaleDateString()}`}
            </p>
          )}
        </div>
        <button onClick={onClose} data-testid="pa-close" className="p-1 text-[#7A8B82] hover:text-red-600">
          <X className="w-4 h-4"/>
        </button>
      </div>
      <div className="flex-1 overflow-auto p-3 space-y-2">
        {!items.length && (
          <p className="text-xs text-[#B4C0BB] italic text-center py-4" data-testid="pa-empty">
            No unresolved actions carried forward.
          </p>
        )}
        {items.map(a => {
          const overdue = _isOverdue(a);
          return (
            <button key={a.id}
                    onClick={() => setDetailId(a.id)}
                    data-testid={`pa-action-${a.id}`}
                    className="w-full text-left border border-[#EEF1EC] rounded-md text-xs bg-white hover:bg-[#F9FBF7] px-2.5 py-2 block">
              <p className="font-medium text-[#1A2421]">{a.task}</p>
              <p className="text-[10px] text-[#7A8B82] mt-0.5">
                {a.owner_text || 'Unassigned'}
                {a.due_date && <> · Due {a.due_date}</>}
                <span className="mx-1">·</span>
                <span className={`uppercase tracking-wider text-[9px] px-1 py-0.5 rounded ${
                  a.status === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                  'bg-amber-100 text-amber-700'}`}>{a.status.replace('_', ' ')}</span>
                {overdue && (
                  <span className="ml-1.5 text-red-600 inline-flex items-center gap-0.5" data-testid={`pa-overdue-${a.id}`}>
                    <AlertTriangle className="w-2.5 h-2.5"/> OVERDUE
                  </span>
                )}
              </p>
              <p className="text-[10px] text-[color:var(--color-primary-hover)] mt-0.5">
                {(a.update_history || []).length} update{(a.update_history || []).length === 1 ? '' : 's'} · click to open
              </p>
            </button>
          );
        })}
      </div>

      {detailId && (
        <ActionDetailModal actionId={detailId}
                             onClose={() => setDetailId(null)}
                             onUpdated={load}/>
      )}
    </div>
  );
}
